import { ProCard } from '@ant-design/pro-components';
import {
  AppConfigContextProvider,
  I18nProvider,
  TaskContent,
  type TaskContentRef,
  TaskCreateButton,
  TaskGroupBy,
  TaskMenuProvider,
  translations,
} from '@zhiyue/task-management';
import { Alert, Button, Space, Spin } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import React, { useRef } from 'react';
import Empty from '@/components/QuietEmpty';
import { useOpenPlatformRuntime } from '@/services/openPlatformToken';

type CrmTaskRelationPanelProps = {
  sourceSceneUuid: string;
  sourceObjectUuid?: string;
  title?: string;
  className?: string;
  onTasksChanged?: () => void | Promise<void>;
};

export function CrmTaskRelationPanel({
  sourceSceneUuid,
  sourceObjectUuid,
  title = '任务',
  className,
  onTasksChanged,
}: CrmTaskRelationPanelProps) {
  const taskContentRef = useRef<TaskContentRef | null>(null);
  const {
    config: openPlatformConfig,
    error,
    retry,
    contextKey,
  } = useOpenPlatformRuntime();
  const sourceAppUuid = String(
    BizAppConfig.taskSourceAppUuid || 'zy-crm',
  ).trim();
  const normalizedSourceObjectUuid = String(sourceObjectUuid || '').trim();
  if (!normalizedSourceObjectUuid) {
    return (
      <ProCard title={title} className={className}>
        <Empty description={false} />
      </ProCard>
    );
  }

  if (!openPlatformConfig)
    return (
      <ProCard title={title}>
        {error ? (
          <Alert
            type="error"
            title="任务认证失败"
            action={<Button onClick={retry}>重试</Button>}
          />
        ) : (
          <Spin />
        )}
      </ProCard>
    );

  return (
    <I18nProvider translations={translations}>
      <AppConfigContextProvider
        key={`${contextKey}:${sourceSceneUuid}:${normalizedSourceObjectUuid}`}
        openPlatformConfig={openPlatformConfig}
      >
        <TaskMenuProvider
          sourceAppUuid={sourceAppUuid}
          sourceSceneUuid={sourceSceneUuid}
          sourceObjectUuid={normalizedSourceObjectUuid}
        >
          <ProCard
            title={title}
            className={className}
            extra={
              <Space size={8}>
                <TaskCreateButton
                  taskType="all"
                  sourceAppUuid={sourceAppUuid}
                  sourceSceneUuid={sourceSceneUuid}
                  sourceObjectUuid={normalizedSourceObjectUuid}
                  buttonSize="small"
                  onSuccess={() => {
                    void taskContentRef.current?.onLoad();
                    void onTasksChanged?.();
                  }}
                />
              </Space>
            }
          >
            <TaskContent
              ref={taskContentRef}
              type="all"
              groupBy={TaskGroupBy.NONE}
              detailDisplayMode="modal"
              sourceAppUuid={sourceAppUuid}
              sourceSceneUuid={sourceSceneUuid}
              sourceObjectUuid={normalizedSourceObjectUuid}
              onTasksChanged={onTasksChanged}
            />
          </ProCard>
        </TaskMenuProvider>
      </AppConfigContextProvider>
    </I18nProvider>
  );
}
